import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useDownloadImportTemplate, usePreviewImport, useImportItems } from '../hooks/useQueries';
import PageHeader from '../components/Layout/PageHeader';
import ImportStepper from '../components/Layout/ImportStepper';
import Modal from '../components/Popup/Modal';
import Toast from '../components/Popup/Toast';
import './itemsImportPage.css';

export default function ItemsImportPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [owners, setOwners] = useState([]);
  const [rowOwners, setRowOwners] = useState({});
  const [projects, setProjects] = useState([]);
  const [rowProjects, setRowProjects] = useState({});
  const [bulkOwnerSelection, setBulkOwnerSelection] = useState(null);
  const [bulkProjectSelection, setBulkProjectSelection] = useState(null);
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isPreviewSubmitting, setIsPreviewSubmitting] = useState(false);
  const [importProgress, setImportProgress] = useState({ show: false, startedAt: null });
  const [importElapsedSeconds, setImportElapsedSeconds] = useState(0);

  const downloadTemplateMutation = useDownloadImportTemplate();
  const previewImportMutation = usePreviewImport();
  const importItemsMutation = useImportItems();

  useEffect(() => {
    if (!importProgress.show || !importProgress.startedAt) {
      setImportElapsedSeconds(0);
      return;
    }

    const tick = () => {
      const seconds = Math.floor((Date.now() - importProgress.startedAt) / 1000);
      setImportElapsedSeconds(Number.isFinite(seconds) && seconds >= 0 ? seconds : 0);
    };

    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [importProgress.show, importProgress.startedAt]);

  const steps = [
    { number: 1, label: 'Download\nTemplate' },
    { number: 2, label: 'Upload\nFile' },
    { number: 3, label: 'Validation\nResults' },
    { number: 4, label: 'Import\nComplete' }
  ];

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const waitForUiPaint = () => new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0);
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });

  const handleDownloadTemplate = async (format = 'xlsx') => {
    try {
      await downloadTemplateMutation.mutateAsync(format);
      showToast(`Import template downloaded successfully (${String(format).toUpperCase()})`, 'success');
      // Automatically advance to next step
      setTimeout(() => {
        setCurrentStep(2);
      }, 500); // Small delay to let user see the success message
    } catch (error) {
      console.error('Error downloading template:', error);
      showToast('Failed to download import template', 'error');
    }
  };

  const handleFileSelect = (file) => {
    if (file && (
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'text/csv'
    )) {
      setUploadedFile(file);
      setValidationResults(null);
      setRowOwners({});
      setOwners([]);
      setRowProjects({});
      setProjects([]);
      setBulkOwnerSelection(null);
      setBulkProjectSelection(null);
      setShowAllWarnings(false);
    } else {
      showToast('Please select a valid Excel (.xlsx, .xls) or CSV file', 'error');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const file = e.dataTransfer?.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleNextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handleBackStep = () => {
    if (currentStep === 3) {
      // Going back from validation clears file
      setUploadedFile(null);
      setValidationResults(null);
      setOwners([]);
      setRowOwners({});
      setProjects([]);
      setRowProjects({});
      setBulkOwnerSelection(null);
      setBulkProjectSelection(null);
      setShowAllWarnings(false);
    }
    setCurrentStep(prev => prev - 1);
  };

  const handlePreview = async () => {
    if (!uploadedFile) return;
    
    try {
      setIsPreviewSubmitting(true);
      // Ensure the button spinner is visible before triggering network/upload work.
      await waitForUiPaint();
      const results = await previewImportMutation.mutateAsync(uploadedFile);
      setValidationResults(results);
      
      // Populate available owners and initial selections
      const availableOwners = results?.choices?.owners || [];
      setOwners(availableOwners);

      const availableProjects = results?.choices?.projects || [];
      setProjects(availableProjects);

      const initialRowOwners = {};
      const initialRowProjects = {};
      if (results?.valid_items) {
        results.valid_items.forEach(item => {
          initialRowOwners[item.row] = item.owner || '';
          initialRowProjects[item.row] = item.project_id || item.projectId || '';
        });
      }
      setRowOwners(initialRowOwners);
      setRowProjects(initialRowProjects);
      setBulkOwnerSelection(null);
      setBulkProjectSelection(null);
      setShowAllWarnings(false);
      
      setCurrentStep(3);
      showToast(`Preview complete: ${results.summary.valid_rows} valid, ${results.summary.error_rows} errors`, 'info');
    } catch (error) {
      console.error('Error previewing import:', error);
      showToast(error.message || 'Failed to preview import', 'error');
    } finally {
      setIsPreviewSubmitting(false);
    }
  };

  const handleImport = async () => {
    if (!uploadedFile || !validationResults) {
      showToast('No file or validation results found. Please re-run the preview.', 'error');
      return;
    }

    const ownerSelections = (validationResults.valid_items || []).map(item => ({
      row: item.row,
      owner: rowOwners[item.row]?.trim() || ''
    }));

    const projectSelections = (validationResults.valid_items || []).map(item => ({
      row: item.row,
      projectId: rowProjects[item.row] || ''
    }));
    
    try {
      setImportProgress({ show: true, startedAt: Date.now() });
      // Give React a chance to render the modal before starting the upload.
      await new Promise((resolve) => setTimeout(resolve, 0));
      const results = await importItemsMutation.mutateAsync({ 
        file: uploadedFile,
        ownerSelections,
        projectSelections
      });
      setImportResults(results);
      setCurrentStep(4);
      showToast(`Successfully imported ${results.imported_count} items`, 'success');
    } catch (error) {
      console.error('Error importing items:', error);
      showToast(error.message || 'Failed to import items', 'error');
    } finally {
      setImportProgress({ show: false, startedAt: null });
    }
  };

  const handleViewItems = () => {
    navigate('/items');
  };

  const renderStep1 = () => (
    <div className="import-step-content">
      <div className="card">
        <div className="card-body text-center">
          <h5 className="card-title">Download Import Template</h5>
          <p className="card-text text-muted">
            Download the template, fill it with your items data, then upload it back
          </p>
          <div className="d-flex justify-content-center gap-4 flex-wrap">
            <button
              className="btn btn-primary"
              onClick={() => handleDownloadTemplate('xlsx')}
              disabled={downloadTemplateMutation.isLoading}
            >
              {downloadTemplateMutation.isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Downloading...
                </>
              ) : (
                <>
                  <i className="bi bi-download me-2"></i>
                  Download Excel (.xlsx)
                </>
              )}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleDownloadTemplate('csv')}
              disabled={downloadTemplateMutation.isLoading}
            >
              <i className="bi bi-download me-2"></i>
              Download CSV (.csv)
            </button>
          </div>
        </div>
      </div>
      <div className="import-actions mt-4">
        <button className="btn btn-primary" onClick={handleNextStep}>
          Next <i className="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="import-step-content">
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Upload Your File</h5>
          
          {!uploadedFile ? (
            <div
              className={`file-upload-area${isDragActive ? ' drag-active' : ''}`}
              onClick={() => document.getElementById('fileInput').click()}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              <div className="upload-content">
                <i className="bi bi-cloud-upload fs-1 text-muted mb-3"></i>
                <h6 className="mb-2">Drop your file here or click to browse</h6>
                <p className="text-muted small">
                  Supports Excel (.xlsx, .xls) and CSV files up to 10MB
                </p>
              </div>
              <input
                id="fileInput"
                type="file"
                onChange={handleFileChange}
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div className="selected-file-info">
              <div className="file-info">
                <i className="bi bi-file-earmark-spreadsheet fs-2 text-success"></i>
                <div className="file-details ms-3">
                  <h6 className="mb-1">{uploadedFile.name}</h6>
                  <small className="text-muted">
                    {(uploadedFile.size / 1024).toFixed(1)} KB • {uploadedFile.type.split('/')[1].toUpperCase()}
                  </small>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => setUploadedFile(null)}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="import-actions mt-4">
        <button className="btn btn-secondary me-2" onClick={handleBackStep}>
          <i className="bi bi-arrow-left me-1"></i> Back
        </button>
        <button
          className="btn btn-primary"
          onClick={handlePreview}
          disabled={!uploadedFile || previewImportMutation.isLoading || isPreviewSubmitting}
        >
          {(previewImportMutation.isLoading || isPreviewSubmitting) ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Validating...
            </>
          ) : (
            <>
              Next <i className="bi bi-arrow-right ms-1"></i>
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (!validationResults) return null;

    const { summary, valid_items, skipped_items, errors, warnings } = validationResults;
    const riskScoreWarningCount = (warnings || []).reduce((count, warningRow) => {
      const rowWarnings = Array.isArray(warningRow?.warnings) ? warningRow.warnings : [];
      const rowRiskWarnings = rowWarnings.filter((msg) => {
        const text = String(msg || '').toLowerCase();
        return text.includes('risk score') || text.includes('numeric score') || text.includes('scored field');
      }).length;
      return count + rowRiskWarnings;
    }, 0);

    const ownerOptions = owners
      .map((owner) => {
        if (owner == null) return null;
        if (typeof owner === 'string') {
          const trimmed = owner.trim();
          if (!trimmed) return null;
          return { value: trimmed, label: trimmed, projectCount: 0 };
        }

        const name = owner.name ? String(owner.name).trim() : '';
        const email = owner.email ? String(owner.email).trim() : '';
        const value = name || email;
        if (!value) return null;

        const projectCount = Number(owner.project_count ?? owner.projectCount ?? 0) || 0;
        const labelBase = name || email;
        const label = projectCount > 0
          ? `${labelBase} (${projectCount} projects)`
          : labelBase;

        return { value, label, projectCount };
      })
      .filter(Boolean)
      .sort((a, b) => (b.projectCount || 0) - (a.projectCount || 0) || a.label.localeCompare(b.label));
    const projectOptions = projects.map(p => ({
      value: String(p.id),
      label: p.name
    }));
    const displayedWarnings = showAllWarnings ? (warnings || []) : (warnings || []).slice(0, 5);

    const applyOwnerToAll = (ownerValue) => {
      const nextOwners = {};
      (valid_items || []).forEach((item) => {
        nextOwners[item.row] = ownerValue;
      });
      setRowOwners((prev) => ({ ...prev, ...nextOwners }));
    };

    const applyProjectToAll = (projectIdValue) => {
      const nextProjects = {};
      (valid_items || []).forEach((item) => {
        nextProjects[item.row] = projectIdValue;
      });
      setRowProjects((prev) => ({ ...prev, ...nextProjects }));
    };

    return (
      <div className="import-step-content">
        <div className={`alert ${riskScoreWarningCount > 0 ? 'alert-warning' : 'alert-info'} mb-3`}>
          <div className="fw-semibold mb-1">Risk Score Input Format</div>
          <div className="small mb-0">
            Risk score fields should be entered as numbers only (for example: 1, 2, 3). Text entries like High/Low will not be imported for those specific fields, but the item itself will still import.
            {riskScoreWarningCount > 0
              ? ` ${riskScoreWarningCount} risk score value${riskScoreWarningCount === 1 ? '' : 's'} in this file will be skipped.`
              : ''}
          </div>
        </div>

        {warnings && warnings.length > 0 && (
          <div className="card mb-3">
            <div className="card-header bg-warning-subtle">
              <h6 className="mb-0">Warnings ({warnings.length})</h6>
            </div>
            <div className="card-body p-2">
              {displayedWarnings.map((warning, index) => (
                <div key={index} className="alert alert-warning alert-sm p-2 mb-1">
                  <strong>Row {warning.rowNumber}:</strong> {(warning.warnings || []).join(', ')}
                </div>
              ))}
              {warnings.length > 5 && (
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => setShowAllWarnings((prev) => !prev)}
                  >
                    {showAllWarnings
                      ? 'Show less'
                      : `Show ${warnings.length - 5} more warning${warnings.length - 5 === 1 ? '' : 's'}`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="import-summary-cards mb-4">
          <div className="summary-grid">
            <div className="summary-card">
              <div className="card text-center">
                <div className="card-body">
                  <div className="fs-4 fw-bold text-success">{summary.valid_rows}</div>
                  <small className="text-muted">Valid Items</small>
                </div>
              </div>
            </div>
            <div className="summary-card">
              <div className="card text-center">
                <div className="card-body">
                  <div className="fs-4 fw-bold text-warning">{summary.skipped_rows}</div>
                  <small className="text-muted">Skipped</small>
                </div>
              </div>
            </div>
            <div className="summary-card">
              <div className="card text-center">
                <div className="card-body">
                  <div className="fs-4 fw-bold text-danger">{summary.error_rows}</div>
                  <small className="text-muted">Errors</small>
                </div>
              </div>
            </div>
            <div className="summary-card">
              <div className="card text-center">
                <div className="card-body">
                  <div className="fs-4 fw-bold text-info">{summary.total_rows}</div>
                  <small className="text-muted">Total Rows</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {valid_items && valid_items.length > 0 && (
          <div className="card mb-3">
            <div className="card-header">
              <h6 className="mb-0">Quick Fill</h6>
            </div>
            <div className="card-body">
              <div className="bulk-assignment-grid">
                <div className="bulk-assignment-row">
                  <div className="bulk-assignment-label">Owner for all valid rows</div>
                  <div className="bulk-assignment-controls">
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable
                      placeholder="Select owner for all..."
                      options={ownerOptions}
                      value={bulkOwnerSelection}
                      onChange={(selected) => setBulkOwnerSelection(selected || null)}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => applyOwnerToAll(bulkOwnerSelection?.value || '')}
                    >
                      Apply to All
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setBulkOwnerSelection(null);
                        applyOwnerToAll('');
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="bulk-assignment-row">
                  <div className="bulk-assignment-label">Project for all valid rows</div>
                  <div className="bulk-assignment-controls">
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable
                      placeholder="Select project for all..."
                      options={projectOptions}
                      value={bulkProjectSelection}
                      onChange={(selected) => setBulkProjectSelection(selected || null)}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => applyProjectToAll(bulkProjectSelection?.value || '')}
                    >
                      Apply to All
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setBulkProjectSelection(null);
                        applyProjectToAll('');
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
              <div className="text-muted small mt-2">
                You can still override owner or project per row in the table below.
              </div>
            </div>
          </div>
        )}

        {valid_items && valid_items.length > 0 && (
          <div className="card mb-3">
            <div className="card-header">
              <h6 className="mb-0">Valid Items ({valid_items.length})</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 import-results-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Title</th>
                      <th>Stage</th>
                      <th className="import-col-owner">Owner</th>
                      <th className="import-col-project">Project</th>
                    </tr>
                  </thead>
                  <tbody>
                    {valid_items.map(item => (
                      <tr key={item.row}>
                        <td>{item.row}</td>
                        <td className="import-col-title">{item.title}</td>
                        <td><span className="badge bg-primary">{item.stage}</span></td>
                        <td>
                          <Select
                            className="react-select-container react-select-sm"
                            classNamePrefix="react-select"
                            isClearable
                            placeholder="Select owner..."
                            options={ownerOptions}
                            value={
                              rowOwners[item.row]
                                ? ownerOptions.find(o => o.value === rowOwners[item.row]) || { value: rowOwners[item.row], label: rowOwners[item.row] }
                                : null
                            }
                            onChange={(selected) =>
                              setRowOwners(prev => ({ ...prev, [item.row]: selected?.value || '' }))
                            }
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </td>
                        <td>
                          <Select
                            className="react-select-container react-select-sm"
                            classNamePrefix="react-select"
                            isClearable
                            placeholder="Select project..."
                            options={projectOptions}
                            value={
                              rowProjects[item.row]
                                ? {
                                    value: String(rowProjects[item.row]),
                                    label: projectOptions.find(p => p.value === String(rowProjects[item.row]))?.label || String(rowProjects[item.row])
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              setRowProjects(prev => ({ ...prev, [item.row]: selected?.value || '' }))
                            }
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(owners.length === 0 || projects.length === 0) && (
                <div className="card-footer text-muted small">
                  {owners.length === 0 && <div>No owners available (owner selection is optional for import).</div>}
                  {projects.length === 0 && <div>No active projects available (project selection is optional for import).</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {errors && errors.length > 0 && (
          <div className="card mb-3">
            <div className="card-header bg-danger text-white">
              <h6 className="mb-0">Errors ({errors.length})</h6>
            </div>
            <div className="card-body p-2">
              {errors.slice(0, 5).map((error, index) => (
                <div key={index} className="alert alert-danger alert-sm p-2 mb-1">
                  <strong>Row {error.rowNumber}:</strong> {error.errors.join(', ')}
                </div>
              ))}
              {errors.length > 5 && (
                <div className="text-muted small">... and {errors.length - 5} more errors</div>
              )}
            </div>
          </div>
        )}

        {skipped_items && skipped_items.length > 0 && (
          <div className="card mb-3">
            <div className="card-header bg-warning">
              <h6 className="mb-0">Skipped Items ({skipped_items.length})</h6>
            </div>
            <div className="card-body p-2">
              {skipped_items.slice(0, 5).map((item, index) => (
                <div key={index} className="alert alert-warning alert-sm p-2 mb-1">
                  <strong>Row {item.rowNumber}:</strong> {item.title} - {item.reason}
                </div>
              ))}
              {skipped_items.length > 5 && (
                <div className="text-muted small">... and {skipped_items.length - 5} more skipped</div>
              )}
            </div>
          </div>
        )}

        <div className="import-actions mt-4">
          <button className="btn btn-secondary me-2" onClick={handleBackStep}>
            <i className="bi bi-arrow-left me-1"></i> Back
          </button>
          <button
            className="btn btn-success"
            onClick={handleImport}
            disabled={summary.valid_rows === 0 || importItemsMutation.isLoading}
          >
            {importItemsMutation.isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Importing...
              </>
            ) : (
              <>
                <i className="bi bi-upload me-2"></i>
                Import {summary.valid_rows} Items
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    if (!importResults) return null;

    return (
      <div className="import-step-content">
        <div className="card text-center">
          <div className="card-body">
            <div className="mb-4">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
            </div>
            <h4 className="text-success">Import Complete!</h4>
            <p className="text-muted mb-4">
              Successfully imported {importResults.imported_count} items.
              {importResults.skipped_count > 0 && (
                <> {importResults.skipped_count} duplicate items were skipped.</>
              )}
            </p>
            <button className="btn btn-primary" onClick={handleViewItems}>
              <i className="bi bi-list-ul me-2"></i>
              View Items
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="items-import-page">
      <div className="container-fluid">
        <PageHeader 
          title="Import Items"
          subtitle="Import multiple items from Excel or CSV files"
        />
        
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="import-wizard-container">
              <ImportStepper steps={steps} currentStep={currentStep} />
              
              <div className="mt-4">
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
                {currentStep === 4 && renderStep4()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toast 
        show={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
        message={toast.message}
        type={toast.type}
      />

      <Modal
        show={importProgress.show}
        title="Import in progress"
        content={(
          <div>
            <div className="d-flex align-items-center gap-3 mb-3">
              <span className="spinner-border" role="status" aria-label="Importing" />
              <div>
                <div style={{ fontWeight: 600 }}>Importing items…</div>
                <div className="text-muted small">
                  {validationResults?.summary?.valid_rows != null
                    ? `Preparing to import ${validationResults.summary.valid_rows} items. `
                    : ''}
                  Elapsed: {Math.floor(importElapsedSeconds / 60)}m {String(importElapsedSeconds % 60).padStart(2, '0')}s
                </div>
              </div>
            </div>

            <div className="progress" style={{ height: '10px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: '100%' }}
                aria-valuenow={100}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>

            <div className="text-muted small mt-3">
              Large imports can take a few minutes. Please keep this window open.
            </div>
          </div>
        )}
        footer={<div />}
        size="modal-dialog-centered modal-lg"
      />
    </div>
  );
}